import { useState, useRef, useEffect } from "preact/compat";
import classNames from 'classnames';
import { arrow, autoUpdate, useFloating, flip, limitShift, offset as floatingOffset, shift, useTransitionStyles, FloatingFocusManager, useHover, useInteractions, useRole, useFocus, useClick, useDismiss } from '@floating-ui/react';
import useId from "../utilities/useId";
import { Button } from "../Button";
import { CloseIconThin } from "../Icons";
import useTooltipAnalytics from "./useTooltipAnalytics";
import mergeRefs from "../utilities/mergeRefs";

// Similarly to the Button component, we want to expand the props type definition to
// permit pass-through props for the most commonly used underlying components.
// However, unlike in Button, we have not removed the ability for applications to
// define a custom `component` prop, which means there are theoretically props
// specific to that component type which will not be available in the TooltipProps
// definition. The strategy here is to keep the types simple by including just the
// possible attributes of button and anchor, but it comes at the expense of accuracy.
// If applications have extra props for their custom components, they will need to
// tell TypeScript to ignore those props for now. We'd like to revisit Tooltip in
// the future and improve it.
import { jsx as _jsx, jsxs as _jsxs } from "preact/compat/jsx-runtime";
export const placements = ['top', 'top-start', 'top-end', 'bottom', 'bottom-start', 'bottom-end', 'right', 'right-start', 'right-end', 'left', 'left-start', 'left-end'];

/**
 * Tooltips provide additional information upon hover, focus or click.
 * For information about how and when to use this component,
 * [refer to its full documentation page](https://design.cms.gov/components/tooltip/).
 *
 * When using the `<TooltipIcon>` as the only child of `<Tooltip>`, be sure to
 * provide an `aria-label` on the `<Tooltip>` to ensure an accessible name for
 * the trigger.
 */

export const Tooltip = props => {
  const {
    dialog,
    onClose,
    onOpen,
    placement = 'top',
    offset = [0, 5],
    transitionDuration = 250
  } = props;
  const contentId = useId('tooltip-trigger--', props.id);
  const arrowElement = useRef(null);
  const {
    contentRef,
    sendTooltipEvent
  } = useTooltipAnalytics(props);
  const setArrowElement = elem => {
    arrowElement.current = elem;
  };
  const [active, setActive] = useState(false);
  const {
    context,
    floatingStyles,
    middlewareData,
    placement: finalPlacement,
    refs
  } = useFloating({
    onOpenChange: setActive,
    open: active,
    placement: placement,
    middleware: [floatingOffset({
      crossAxis: offset[0],
      mainAxis: offset[1]
    }), flip(), shift({
      limiter: limitShift()
    }), arrow({
      element: arrowElement.current
    })],
    whileElementsMounted: autoUpdate
  });
  const click = useClick(context, {
    enabled: dialog
  });
  const dismiss = useDismiss(context, {
    enabled: dialog
  });
  const focus = useFocus(context, {
    enabled: !dialog
  });
  const hover = useHover(context, {
    enabled: !dialog
  });
  const role = useRole(context, {
    role: dialog ? 'dialog' : 'tooltip'
  });
  const {
    getReferenceProps,
    getFloatingProps
  } = useInteractions([click, dismiss, focus, hover, role]);
  const {
    isMounted,
    styles
  } = useTransitionStyles(context, {
    duration: transitionDuration
  });
  const side = finalPlacement.split('-')[0];
  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right'
  }[side];
  const handleCloseButtonClick = () => {
    if (active && props.dialog) {
      setActive(false);
    }
  };
  useEffect(() => {
    if (active) {
      onOpen && onOpen();
      sendTooltipEvent();
    } else {
      onClose && onClose();
    }
  }, [active, onClose, onOpen, sendTooltipEvent]);
  const renderTrigger = props => {
    const {
      activeClassName,
      analytics,
      analyticsLabelOverride,
      onAnalyticsEvent,
      ariaLabel,
      children,
      className,
      component = 'button',
      dialog,
      id,
      offset,
      onClose,
      onOpen,
      inversed,
      interactiveBorder,
      placement,
      maxWidth,
      title,
      transitionDuration,
      triggerAriaLabel,
      zIndex,
      showCloseButton,
      closeButtonLabel,
      contentHeading,
      ...others
    } = props;
    const TriggerComponent = component;
    const triggerClasses = classNames('ds-c-tooltip__trigger', className, {
      [activeClassName]: activeClassName && active,
      'ds-c-tooltip__trigger--inverse': inversed
    });
    const linkTriggerOverrides = {
      tabIndex: TriggerComponent === 'a' ? 0 : undefined
    };
    return /*#__PURE__*/_jsx(TriggerComponent, {
      type: TriggerComponent === 'button' ? 'button' : undefined,
      "aria-label": ariaLabel || triggerAriaLabel || undefined,
      "aria-describedby": dialog ? undefined : contentId,
      className: triggerClasses,
      ref: mergeRefs([contentRef, refs.setReference]),
      ...others,
      ...linkTriggerOverrides,
      ...getReferenceProps(),
      children: children
    });
  };
  const renderContent = props => {
    const {
      closeButtonLabel,
      dialog,
      contentHeading,
      inversed,
      interactiveBorder = 15,
      maxWidth = '300px',
      showCloseButton,
      title,
      zIndex = 9999
    } = props;
    const tooltipStyle = {
      maxWidth,
      zIndex
    };
    const interactiveBorderStyle = {
      left: `-${interactiveBorder}px`,
      top: `-${interactiveBorder}px`,
      border: `${interactiveBorder}px solid transparent`,
      zIndex: -999 // ensures interactive border doesnt cover tooltip content
    };
    const tooltipContent = /*#__PURE__*/_jsxs("div", {
      id: contentId,
      tabIndex: dialog ? -1 : null,
      ref: refs.setFloating,
      className: classNames('ds-c-tooltip', {
        'ds-c-tooltip--inverse': inversed
      }),
      style: {
        ...tooltipStyle,
        ...floatingStyles,
        ...styles
      },
      "data-placement": finalPlacement,
      "aria-hidden": !active,
      ...getFloatingProps(),
      children: [/*#__PURE__*/_jsx("span", {
        className: "ds-c-tooltip__arrow",
        ref: setArrowElement,
        style: {
          left: middlewareData.arrow?.x,
          top: middlewareData.arrow?.y,
          [staticSide]: '-5px'
        }
      }), /*#__PURE__*/_jsxs("div", {
        className: "ds-c-tooltip__content",
        children: [contentHeading || showCloseButton ? /*#__PURE__*/_jsxs("div", {
          className: classNames('ds-c-tooltip__header', {
            'ds-c-tooltip__header--right': !contentHeading
          }),
          children: [contentHeading, showCloseButton && /*#__PURE__*/_jsx(Button, {
            variation: "ghost",
            size: "small",
            className: "ds-c-tooltip__close-button",
            onClick: handleCloseButtonClick,
            "aria-label": closeButtonLabel || 'Close',
            children: /*#__PURE__*/_jsx(CloseIconThin, {})
          })]
        }) : null, title]
      }), !dialog && /*#__PURE__*/_jsx("span", {
        className: "ds-c-tooltip__interactive-border",
        style: interactiveBorderStyle
      })]
    });
    return isMounted && tooltipContent;
  };
  return /*#__PURE__*/_jsxs("div", {
    className: "ds-c-tooltip__container",
    children: [renderTrigger(props), dialog ? /*#__PURE__*/_jsx(FloatingFocusManager, {
      context: context,
      initialFocus: refs.floating,
      guards: false,
      children: renderContent(props)
    }) : renderContent(props)]
  });
};
export default Tooltip;